import type { AppLayout, FieldDef, LayoutFieldSlot, LayoutPage, LayoutSection } from "../../lib/opsApi";
import type { RecordKind } from "./RecordWorkspace";

export type { RecordKind } from "./RecordWorkspace";

export const DESIGNER_SCREENS = [
  { key: "company", label: "Companies" },
  { key: "contact", label: "Contacts" },
  { key: "lead", label: "Leads" },
  { key: "product", label: "Products" },
  { key: "opportunity", label: "Opportunities" },
  { key: "quote", label: "Quotes" },
  { key: "order", label: "Orders" },
  { key: "invoice", label: "Invoices" },
  { key: "sales_cycle", label: "Sales cycle" },
  { key: "activity", label: "Activities" },
  { key: "support", label: "Support" },
  { key: "calendar", label: "Calendar" },
  { key: "campaign", label: "Marketing" },
  { key: "forecast", label: "Forecast & KPI" },
] as const;

export const FIELD_TYPES = ["text", "email", "phone", "number", "currency", "date", "boolean", "select", "lookup", "json"] as const;

export interface ScreenPreview {
  kind: RecordKind;
  objectLabel: string;
  title: string;
  subtitle?: string;
  backTo: string;
  backLabel: string;
  highlights: Array<{ label: string; value: string }>;
  actions: Array<{ label: string; variant?: "secondary" }>;
}

export const SCREEN_PREVIEWS: Record<string, ScreenPreview> = {
  company: {
    kind: "company",
    objectLabel: "Company",
    title: "Sample company",
    subtitle: "Trading name",
    backTo: "/apps/crm/companies",
    backLabel: "← Back to Companies",
    highlights: [
      { label: "Billing address", value: "City, Country" },
      { label: "Status", value: "prospect" },
      { label: "Company code", value: "CMP0001" },
      { label: "Industry", value: "Industry" },
      { label: "Phone", value: "+1 555 0100" },
      { label: "Email", value: "hello@example.com" },
    ],
    actions: [
      { label: "New lead" },
      { label: "New support ticket", variant: "secondary" },
      { label: "Start sales cycle", variant: "secondary" },
      { label: "Edit", variant: "secondary" },
    ],
  },
  contact: {
    kind: "contact",
    objectLabel: "Contact",
    title: "Sample contact",
    backTo: "/apps/crm/companies",
    backLabel: "← Back to Companies",
    highlights: [
      { label: "Work email", value: "work@example.com" },
      { label: "Marketing", value: "Yes" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  lead: {
    kind: "lead",
    objectLabel: "Lead",
    title: "Sample lead",
    subtitle: "Sample company",
    backTo: "/apps/crm/leads",
    backLabel: "← Back to Leads",
    highlights: [
      { label: "Lead ID", value: "LED0001" },
      { label: "Status", value: "new" },
      { label: "Temperature", value: "cold" },
      { label: "Company", value: "Sample company" },
      { label: "Email", value: "lead@example.com" },
    ],
    actions: [{ label: "Convert" }],
  },
  product: {
    kind: "product",
    objectLabel: "Product",
    title: "Sample product",
    backTo: "/apps/crm/products",
    backLabel: "← Back to Products",
    highlights: [
      { label: "SKU", value: "SKU-1" },
      { label: "List price", value: "$0.00" },
      { label: "Status", value: "in stock" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  opportunity: {
    kind: "opportunity",
    objectLabel: "Opportunity",
    title: "Sample opportunity",
    backTo: "/apps/crm/opportunities",
    backLabel: "← Back to Opportunities",
    highlights: [
      { label: "Opportunity ID", value: "OPP0001" },
      { label: "Stage", value: "Lead In" },
      { label: "Amount", value: "$0" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  quote: {
    kind: "quote",
    objectLabel: "Quote",
    title: "Q-0001",
    backTo: "/apps/crm/quotes",
    backLabel: "← Back to Quotes",
    highlights: [
      { label: "Status", value: "draft" },
      { label: "Total", value: "$0.00" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  order: {
    kind: "order",
    objectLabel: "Order",
    title: "SO-0001",
    backTo: "/apps/crm/orders",
    backLabel: "← Back to Orders",
    highlights: [
      { label: "Status", value: "open" },
      { label: "Total", value: "$0.00" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  activity: {
    kind: "activity",
    objectLabel: "Activity",
    title: "Sample activity",
    backTo: "/apps/crm/companies",
    backLabel: "← Back",
    highlights: [
      { label: "Type", value: "call" },
      { label: "Outcome", value: "—" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  invoice: {
    kind: "invoice",
    objectLabel: "Invoice",
    title: "INV-0001",
    backTo: "/apps/crm/invoices",
    backLabel: "← Back to Invoices",
    highlights: [
      { label: "Status", value: "issued" },
      { label: "Total", value: "$0.00" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  sales_cycle: {
    kind: "sales_cycle",
    objectLabel: "Sales cycle",
    title: "Sample sales cycle",
    backTo: "/apps/crm/sales-cycle",
    backLabel: "← Back to Sales cycle",
    highlights: [
      { label: "Status", value: "active" },
      { label: "Company", value: "Sample company" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  support: {
    kind: "support",
    objectLabel: "Support",
    title: "Sample ticket",
    backTo: "/apps/crm/support",
    backLabel: "← Back to Support",
    highlights: [
      { label: "Status", value: "open" },
      { label: "Company", value: "Sample company" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  calendar: {
    kind: "calendar",
    objectLabel: "Calendar",
    title: "Sample event",
    backTo: "/apps/crm/calendar",
    backLabel: "← Back to Calendar",
    highlights: [
      { label: "Starts", value: "Tomorrow" },
      { label: "Company", value: "Sample company" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  campaign: {
    kind: "campaign",
    objectLabel: "Marketing",
    title: "Sample campaign",
    backTo: "/apps/crm/marketing",
    backLabel: "← Back to Marketing",
    highlights: [
      { label: "Status", value: "draft" },
      { label: "Audience", value: "0" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
  forecast: {
    kind: "forecast",
    objectLabel: "Forecast & KPI",
    title: "Team forecast",
    backTo: "/apps/crm/forecast",
    backLabel: "← Back to Forecast",
    highlights: [
      { label: "Weighted", value: "$0" },
      { label: "Target", value: "$0" },
    ],
    actions: [{ label: "Edit", variant: "secondary" }],
  },
};

const SAMPLE_VALUES: Record<string, Record<string, string>> = {
  company: {
    legal_name: "Sample company",
    trading_name: "Trading name",
    company_code: "CMP0001",
    account_status: "prospect",
    industry: "Industry",
    website: "https://example.com",
    primary_phone: "+1 555 0100",
    primary_email: "hello@example.com",
    registration_number: "123456",
    vat_number: "VAT-1",
    currency: "USD",
    payment_terms: "Net 30",
    billing_address_line1: "1 Example Street",
    billing_address_city: "City",
    billing_address_state: "State",
    billing_address_country: "US",
    notes: "Notes about this company",
  },
  contact: {
    first_name: "Mina",
    last_name: "Chen",
    work_email: "mina@example.com",
    personal_email: "mina.personal@example.com",
    marketing_enabled: "Yes",
    gender: "—",
  },
  lead: {
    first_name: "Alex",
    last_name: "River",
    email: "lead@example.com",
    phone: "+1 555 0199",
    lead_status: "new",
    rating: "cold",
    lead_score: "12",
    lead_source: "Web",
    company_name: "Sample company",
    notes: "Follow up this week",
  },
  product: {
    name: "Sample product",
    sku: "SKU-1",
    description: "Product description",
    unit_price: "10",
    product_category: "Hardware",
    product_status: "in_stock",
  },
};

function visualSpan(raw: number, pairLegacyWide: boolean): number {
  if (pairLegacyWide) return 1;
  return raw >= 4 ? 2 : raw <= 1 ? 1 : raw;
}

function normalizeSlot(slot: LayoutFieldSlot, pairLegacyWide = false): LayoutFieldSlot {
  return {
    field_key: slot.field_key,
    canvas_row: slot.canvas_row || 1,
    col_start: slot.col_start || 1,
    col_span: visualSpan(slot.col_span || 1, pairLegacyWide),
  };
}

export function flattenPageFields(page: LayoutPage): LayoutFieldSlot[] {
  if (page.sections?.length) {
    return page.sections.flatMap((section) => section.fields);
  }
  return page.fields ?? [];
}

export function normalizeLayoutPages(pages: LayoutPage[] | undefined): LayoutPage[] {
  if (!pages?.length) return [];
  return pages.map((page) => {
    const rawSections = page.sections;
    let sections: LayoutSection[];
    if (!rawSections?.length) {
      const rawFields = page.fields ?? [];
      const pairLegacy = rawFields.length > 0 && rawFields.every((item) => (item.col_span || 1) >= 4);
      sections = [
        {
          key: page.key || "details",
          label: page.label || "Details",
          fields: rawFields.map((item) => normalizeSlot(item, pairLegacy)),
        },
      ];
    } else {
      sections = rawSections.map((section) => ({
        key: section.key,
        label: section.label,
        fields: (section.fields ?? []).map((item) => normalizeSlot(item)),
      }));
    }
    const fields = sections.flatMap((section) => section.fields);
    const kind = page.kind === "fields" || page.kind === "related"
      ? page.kind
      : fields.length
        ? "fields"
        : "related";
    return { key: page.key, label: page.label, kind, sections, fields };
  });
}

export function withNormalizedLayout(layout: AppLayout): AppLayout {
  return {
    ...layout,
    layout: { pages: normalizeLayoutPages(layout.layout.pages) },
  };
}

export function placedFieldKeys(layout: AppLayout | null, pageKey?: string): Set<string> {
  const pages = layout ? normalizeLayoutPages(layout.layout.pages) : [];
  const page = pageKey ? pages.find((item) => item.key === pageKey) : pages[0];
  return new Set(flattenPageFields(page ?? { key: "details", label: "Details", fields: [] }).map((item) => item.field_key));
}

export function recordFieldValue(record: Record<string, unknown> | null | undefined, fieldKey: string): unknown {
  if (!record) return undefined;
  if (Object.prototype.hasOwnProperty.call(record, fieldKey)) {
    const direct = record[fieldKey];
    if (direct != null && direct !== "") return direct;
  }
  const custom = record.custom_fields;
  if (custom && typeof custom === "object") {
    const nested = (custom as Record<string, unknown>)[fieldKey];
    if (nested != null && nested !== "") return nested;
  }
  return record[fieldKey];
}

export function formatFieldValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function sampleFieldValue(entity: string, field: FieldDef | undefined): string {
  if (!field) return "—";
  return SAMPLE_VALUES[entity]?.[field.field_key] ?? field.field_type ?? "—";
}

export function findField(fields: FieldDef[], fieldKey: string): FieldDef | undefined {
  return fields.find((item) => item.field_key === fieldKey);
}

export function slotIsWide(slot: LayoutFieldSlot): boolean {
  return (slot.col_span || 1) >= 2;
}
